import React from 'react';
import SEO from '../components/SEO';
import StrategistPanel from '../components/StrategistPanel';
import CoreValues from '../components/CoreValues';
import CTABanner from '../components/CTABanner';
import Footer from '../components/Footer';
import { ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const milestones = [
  { year: '2018', title: 'First Kit.com build', body: 'Migrated a podcast newsletter from Mailchimp; doubled their open rate in 6 weeks.' },
  { year: '2020', title: 'Top Rated on Upwork, 5.0 rating', body: 'Crossed 1,000 hours of email work and the rare 100% Job Success badge.' },
  { year: '2022', title: 'Newsletter monetization playbook', body: 'Codified the 9-step framework now used across every retainer client.' },
  { year: '2024', title: 'Boutique studio model', body: 'Capped client load at 6 concurrent retainers — quality > scale.' },
  { year: '2026', title: 'Open-sourced field notes', body: 'Started the public blog and lightweight cohort training for solo operators.' },
];

const About = () => {
  return (
    <>
      <SEO
        title="About Akash Tiwari · Growth Hacking"
        description="A boutique email-marketing studio run by Akash Tiwari. Seven years, six concurrent retainers, one obsession: revenue per subscriber."
        canonical="/about"
      />
      <main className="bg-cream pt-28">
        {/* Hero */}
        <section className="container-x section pt-12" data-testid="about-hero">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-2 border border-line text-xs font-semibold uppercase tracking-[0.2em] text-ink">
                Studio
              </span>
              <h1 className="font-display text-5xl md:text-7xl font-extrabold mt-6 leading-[0.98] text-ink tracking-tighter">
                Boutique by design.{' '}
                <span className="font-serif italic text-[var(--gh-orange)]">
                  Built for inbox revenue.
                </span>
              </h1>
            </div>
            <p className="lg:col-span-4 text-lg text-muted-warm">
              Growth Hacking is a one-person studio for creators and boutique brands who
              want a real partner — not a Slack channel full of strangers.
            </p>
          </div>
        </section>

        <StrategistPanel />

        {/* Timeline */}
        <section className="container-x section" data-testid="about-timeline">
          <div className="max-w-3xl">
            <p className="text-xs uppercase tracking-[0.25em] text-[var(--gh-orange)] font-semibold">
              Journey
            </p>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold mt-3 text-ink leading-[1.05]">
              Seven quiet years of inbox craft.
            </h2>
          </div>

          <ol className="mt-12 relative border-l-2 border-line pl-6 md:pl-10 space-y-10 max-w-3xl">
            {milestones.map((m, i) => (
              <li key={m.year} data-testid={`milestone-${i}`} className="relative">
                <span className="absolute -left-[2.05rem] md:-left-[2.65rem] top-1 w-4 h-4 rounded-full bg-[var(--gh-orange)] ring-4 ring-cream" />
                <p className="text-xs uppercase tracking-widest text-muted-warm font-semibold">{m.year}</p>
                <h3 className="font-display text-2xl font-bold text-ink mt-1">{m.title}</h3>
                <p className="mt-2 text-base text-muted-warm leading-relaxed">{m.body}</p>
              </li>
            ))}
          </ol>

          <div className="mt-12">
            <Link to="/case-studies" className="btn-orange" data-testid="about-case-studies-link">
              See client outcomes <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        <CoreValues />
        <CTABanner
          eyebrow="Curious if we'd click?"
          title="Let's see if we'd actually enjoy working together."
          body="A 30-minute call. No deck. We look at your numbers, I tell you what I'd ship in week one, and you decide."
        />
      </main>
      <Footer />
    </>
  );
};

export default About;
